import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Send,
  User,
  Mail,
  Phone,
  CheckCircle,
  Sparkles,
  Users,
  BookOpen,
  Calendar,
  Video,
  HelpCircle,
  TrendingUp,
  Building2,
  Bot,
  Target,
  Megaphone,
  Award,
  Briefcase
} from "lucide-react";
import { Button } from "../components/ui/button";
import { aboutData } from "../data/mock";

// Sumanta's image from assets
const SUMANTA_IMAGE = "https://customer-assets-cm19k8pv.emergentagent.net/job_2e3e41cb-8232-4fca-af7d-2026206249af/artifacts/d8xwzyd9_ChatGPT%20Image%20Sep%2013%2C%202026%2C%2007_10_55%20PM.png";

// WhatsApp Channel Link
const WHATSAPP_CHANNEL = "https://whatsapp.com/channel/0029Vb4Ggp7BqbrIIt3Qc43y";

// WhatsApp Icon SVG
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// What you'll get
const benefits = [
  { icon: Video, title: "Webinars", desc: "Live sessions on marketing & business growth" },
  { icon: Users, title: "Meetup Sessions", desc: "Network with like-minded entrepreneurs" },
  { icon: HelpCircle, title: "Consult Sessions", desc: "Get your questions answered directly" },
  { icon: BookOpen, title: "Learning Resources", desc: "Curated guides, tutorials & case studies" },
  { icon: Calendar, title: "Q&A Sessions", desc: "Weekly doubt-clearing sessions" },
  { icon: Bot, title: "AI Updates", desc: "Stay ahead with latest AI tools & trends" }
];

// Topics covered
const topics = [
  { icon: Megaphone, text: "Digital Marketing" },
  { icon: TrendingUp, text: "Sales & Growth" },
  { icon: Building2, text: "Agency Building" },
  { icon: Target, text: "Business Strategy" },
  { icon: Bot, text: "AI & Automation" },
  { icon: Briefcase, text: "Entrepreneurship" }
];

export default function WhatsAppCommunityPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    profession: "",
    experience: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/community/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit');
      }
      
      setSubmitted(true);
    } catch (err) {
      console.error("Submission error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        
        {/* Hero Section */}
        <section className="text-center mb-10 sm:mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Badge with WhatsApp Icon */}
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs sm:text-sm mb-6">
              <WhatsAppIcon className="w-4 h-4" />
              Free · WhatsApp Community
            </span>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Grow with <span className="text-white/60">Sumanta</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Join our exclusive WhatsApp community for entrepreneurs & marketers. 
              Get weekly insights on marketing, sales, business building, and AI — straight to your phone.
            </p>

            {/* Topics Pills */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8">
              {topics.map((topic, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-full border border-white/10 text-xs sm:text-sm text-gray-300"
                >
                  <topic.icon className="w-3.5 h-3.5 text-gray-400" />
                  <span>{topic.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Founder Section */}
        <section className="mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white/[0.02] rounded-2xl p-6 sm:p-8 border border-white/10">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                {/* Founder Image */}
                <div className="shrink-0">
                  <img
                    src={SUMANTA_IMAGE}
                    alt="Sumanta Dey"
                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover border border-white/10"
                  />
                </div>
                
                {/* Founder Info */}
                <div className="text-center sm:text-left">
                  <h3 className="text-xl sm:text-2xl font-bold mb-1">{aboutData.founder.name}</h3>
                  <p className="text-gray-400 text-sm mb-3">{aboutData.founder.title}, Cube Multimedia</p>
                  <p className="text-gray-300 text-sm sm:text-base mb-4">
                    {aboutData.founder.bio}
                  </p>
                  <div className="flex flex-wrap justify-center sm:justify-start gap-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/5 rounded-full text-xs text-gray-400">
                      <Award className="w-3.5 h-3.5" />
                      Est. {aboutData.founder.established}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/5 rounded-full text-xs text-gray-400">
                      <TrendingUp className="w-3.5 h-3.5" />
                      {aboutData.founder.experience} Experience
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/5 rounded-full text-xs text-gray-400">
                      <Users className="w-3.5 h-3.5" />
                      500+ Clients
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          
          {/* Left Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {!submitted ? (
              <div className="bg-white/[0.02] rounded-2xl p-6 sm:p-8 border border-white/10">
                {/* Form Header */}
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-white/10">
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                    <WhatsAppIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm sm:text-base">Join the Community</p>
                    <p className="text-xs text-gray-500">Fill in to get access</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Name */}
                  <div className="mb-4">
                    <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-400">
                      Your name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-400">
                      Email address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div className="mb-4">
                    <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-400">
                      Phone number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors"
                    />
                  </div>

                  {/* Profession */}
                  <div className="mb-4">
                    <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-400">
                      What describes you best?
                    </label>
                    <select
                      name="profession"
                      value={formData.profession}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-white/30 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-black">Select your role</option>
                      <option value="Business Owner" className="bg-black">Business Owner</option>
                      <option value="Startup Founder" className="bg-black">Startup Founder</option>
                      <option value="Agency Owner" className="bg-black">Agency Owner</option>
                      <option value="Freelancer" className="bg-black">Freelancer</option>
                      <option value="Marketing Professional" className="bg-black">Marketing Professional</option>
                      <option value="Student" className="bg-black">Student</option>
                      <option value="Working Professional" className="bg-black">Working Professional</option>
                      <option value="Other" className="bg-black">Other</option>
                    </select>
                  </div>

                  {/* Experience */}
                  <div className="mb-6">
                    <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-400">
                      Years of experience
                    </label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-white/30 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-black">Select experience</option>
                      <option value="0-1 years" className="bg-black">0-1 years</option>
                      <option value="1-3 years" className="bg-black">1-3 years</option>
                      <option value="3-5 years" className="bg-black">3-5 years</option>
                      <option value="5-10 years" className="bg-black">5-10 years</option>
                      <option value="10+ years" className="bg-black">10+ years</option>
                    </select>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                      <p className="text-xs text-red-400">{error}</p>
                    </div>
                  )}

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-white hover:bg-gray-100 disabled:opacity-50 text-black py-4 rounded-xl font-semibold text-sm sm:text-base flex items-center justify-center gap-2 transition-all"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Get Instant Access
                        <span className="ml-1">→</span>
                      </>
                    )}
                  </Button>

                  <p className="text-[10px] sm:text-xs text-gray-600 text-center mt-4">
                    Your data is safe. No spam, ever.
                  </p>
                </form>
              </div>
            ) : (
              /* Success State - Show WhatsApp Button */
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white/[0.02] rounded-2xl p-6 sm:p-8 border border-white/20 text-center"
              >
                <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold mb-3">You're In!</h2>
                <p className="text-gray-400 text-sm sm:text-base mb-6">
                  Welcome to Grow with Sumanta, {formData.name.split(' ')[0]}! 
                  Click below to join our WhatsApp channel.
                </p>
                
                <a
                  href={WHATSAPP_CHANNEL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 w-full bg-white hover:bg-gray-100 text-black py-4 px-6 rounded-xl font-semibold text-base transition-all"
                >
                  <WhatsAppIcon className="w-6 h-6" />
                  Join WhatsApp Channel
                </a>

                <p className="text-xs text-gray-600 mt-4">
                  See you inside the community!
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Right Column - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:sticky lg:top-8"
          >
            <h2 className="text-xl sm:text-2xl font-bold mb-6">
              What You'll <span className="text-gray-500">Get</span>
            </h2>
            
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex gap-4 p-4 bg-white/[0.02] rounded-xl border border-white/5 hover:border-white/10 transition-colors"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                    <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm sm:text-base mb-1">{benefit.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-500">{benefit.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Extra Note */}
            <div className="mt-6 p-4 bg-white/[0.02] rounded-xl border border-white/10">
              <p className="text-xs sm:text-sm text-gray-400">
                <strong className="text-white">100% Free.</strong> No catches. 
                Just pure value for entrepreneurs who want to grow their business and stay ahead of the curve.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-white/10 text-center">
          <p className="text-xs text-gray-600">
            © 2026 Cube Multimedia. An initiative by Sumanta Dey.
          </p>
        </footer>
      </div>
    </div>
  );
}
